import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import Styled from 'styled-components';
import {Button, EMPHASIS, FlexColumn, FlexRow, SubHeading} from '@forecast-it/design-system';
import {CommitMutation} from '../../../../../mutations/CommitMutation';
import {useRouterPrompt} from '../../../../shared/hooks/useRouterPrompt';
import {BASELINE_TEMPLATE_NAME_TAKEN} from '../../../../shared/util/util';
import {BASELINE_TEMPLATES_PATH} from '../../templatesPaths';
import CreateBaselineTemplateMutation from './CreateBaselineTemplateMutation';
import {closeDiscardTemplateChangesModal, showDiscardTemplateChangesModal} from './DiscardTemplateChangesModal';
import {TemplateDetailsCard} from './TemplateDetailsCard';
import {PhaseEditor} from './PhaseEditor';
import {TemplateSummaryPanel} from './TemplateSummaryPanel';
import {
	addPhase,
	blankTemplate,
	FieldErrors,
	isDirty,
	isNameTaken,
	normalise,
	Option,
	PhaseDraft,
	removePhase,
	TemplateDraft,
	untouchedRowKeys,
	updatePhase,
	validate,
} from './baselineTemplateDraft';

const Editor = Styled(FlexColumn)`
	flex: 1;
	min-width: 0;
`;

const NAME_TAKEN_ERROR = 'baseline_templates.errors.name_taken';

const scrollToField = (path: string) => {
	document.querySelector(`[data-field-path="${path}"]`)?.scrollIntoView({behavior: 'smooth', block: 'center'});
};

type Props = {roles: Option[]; categories: Option[]; currency: string; existingNames: readonly string[]};

export const CreateBaselineTemplateForm = ({roles, categories, currency, existingNames}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const history = useHistory();
	const [draft, setDraft] = useState<TemplateDraft>(blankTemplate);
	const [errors, setErrors] = useState<FieldErrors>({});
	const [submitting, setSubmitting] = useState(false);
	const [created, setCreated] = useState(false);

	const {showPrompt, handleDiscard, closePrompt} = useRouterPrompt({hasChanges: isDirty(draft) && !created});
	// The handlers change identity as the blocked location changes; the modal must call the latest.
	const promptHandlers = useRef({handleDiscard, closePrompt});
	promptHandlers.current = {handleDiscard, closePrompt};
	const promptOpen = useRef(false);
	promptOpen.current = showPrompt;
	useEffect(() => {
		if (showPrompt) {
			showDiscardTemplateChangesModal({
				onDiscard: () => promptHandlers.current.handleDiscard(),
				onKeepEditing: () => promptHandlers.current.closePrompt(),
			});
		}
		// Only a newly shown prompt opens the modal; the handlers are read through the ref.
	}, [showPrompt]);

	// Runs after useRouterPrompt has dropped its blocker for the now-clean state.
	useEffect(() => {
		if (created) {
			// A discard modal opened while the request was in flight has nothing left to guard.
			if (promptOpen.current) {
				closeDiscardTemplateChangesModal();
			}
			closePrompt();
			history.push(BASELINE_TEMPLATES_PATH);
		}
		// Must fire only when the template has just been created.
	}, [created]);

	const fieldError = (path: string): string | undefined => (errors[path] ? formatMessage({id: errors[path]}) : undefined);

	const clearErrors = (shouldClear: (path: string) => boolean) => {
		setErrors(current => {
			const cleared = Object.keys(current).filter(shouldClear);
			if (cleared.length === 0) {
				return current;
			}
			const rest = {...current};
			cleared.forEach(path => delete rest[path]);
			return rest;
		});
	};

	const changeTemplate = (patch: Partial<TemplateDraft>, changedPath?: string) => {
		setDraft(current => ({...current, ...patch}));
		clearErrors(path => path === changedPath);
	};

	// A row edited back to untouched is dropped on create, so none of its errors still apply.
	const changePhase = (phaseKey: string) => (patch: Partial<PhaseDraft>, changedPath?: string) => {
		setDraft(current => updatePhase(current, phaseKey, patch));
		const untouched = untouchedRowKeys(patch);
		clearErrors(path => path === changedPath || untouched.some(key => path.startsWith(`${key}.`)));
	};

	const onNameBlur = () => {
		if (isNameTaken(draft.name, existingNames)) {
			setErrors(current => ({...current, name: NAME_TAKEN_ERROR}));
		}
	};

	const onCreate = () => {
		const found = validate(draft, existingNames);
		setErrors(found);
		const firstInvalid = Object.keys(found)[0];
		if (firstInvalid) {
			scrollToField(firstInvalid);
			return;
		}
		setSubmitting(true);
		CommitMutation(
			CreateBaselineTemplateMutation,
			normalise(draft),
			() => setCreated(true),
			// No automatic retry: create is not idempotent, so a resend after a lost response would hit
			// the unique name index and report a name the first request had already saved.
			false,
			null,
			(error, onFailure) => {
				setSubmitting(false);
				if (String(error).includes(`${BASELINE_TEMPLATE_NAME_TAKEN}:`)) {
					setErrors({name: NAME_TAKEN_ERROR});
					scrollToField('name');
					return;
				}
				onFailure?.(error);
			}
		);
	};

	return (
		<FlexRow gap="xl" alignItems="flex-start" data-cy="baseline-template-form">
			<Editor gap="l">
				<TemplateDetailsCard draft={draft} fieldError={fieldError} onChange={changeTemplate} onNameBlur={onNameBlur} />
				<FlexRow justifyContent="space-between" alignItems="center">
					<SubHeading>{formatMessage({id: 'common.phases'})}</SubHeading>
					<Button
						emphasis={EMPHASIS.MONO}
						icon="plus"
						onPress={() => setDraft(current => addPhase(current))}
						data-cy="baseline-template-add-phase"
					>
						{formatMessage({id: 'baseline_templates.add_phase'})}
					</Button>
				</FlexRow>
				{draft.phases.map((phase, index) => (
					<PhaseEditor
						key={phase.key}
						phase={phase}
						index={index}
						includeDeliverables={draft.includeDeliverables}
						canDelete={draft.phases.length > 1}
						roles={roles}
						categories={categories}
						currency={currency}
						fieldError={fieldError}
						onChange={changePhase(phase.key)}
						onDelete={() => setDraft(current => removePhase(current, phase.key))}
					/>
				))}
			</Editor>
			<TemplateSummaryPanel
				draft={draft}
				submitting={submitting}
				onCreate={onCreate}
				onCancel={() => history.push(BASELINE_TEMPLATES_PATH)}
			/>
		</FlexRow>
	);
};
